import { useEffect, useState } from "react";
import { Trophy } from "lucide-react";
import api from "../lib/api";
import { getWithRetry } from "../lib/fetchWithRetry";
import { toStatsQuery } from "../lib/statsQuery";
import { careerSnapshotSummary } from "../lib/careerSnapshotSummary";
import LoadFailureNote from "./LoadFailureNote";
import { SectionCard } from "./identityForm";
import StatsFilterBar from "./StatsFilterBar";
import CareerStatSections from "./CareerStatSections";

/**
 * Issue #460 — the Coach Portal's Career snapshot, grown from six inline
 * tiles into the player profile's own three dashboards behind the player
 * profile's own filters.
 *
 * Martin: *"Career snapshot should have the same filters as per the player
 * profile and should include 'Career highlights', 'Bowling record' and
 * 'Fielding & Wicket Keeping' dashboard."* "Same" is implemented as SHARED,
 * not similar: the sections are `CareerStatSections` and the filter row is
 * `StatsFilterBar`, both extracted from `pages/Statistics.jsx`, and every
 * number comes from the same `/stats/summary` computation the player sees
 * — just with `?player_id=` on the request. No new authorization: that
 * param has always gone through `resolveScope` (api/src/routes/stats.ts),
 * which admits a coach only through an ACTIVE link, the same check as
 * every other read on this portal; the three option endpoints
 * (`/matches/{seasons,competitions,teams}`) take the identical param
 * through the identical scope (issues #55/#78).
 *
 * WHY THIS COMPONENT FETCHES FOR ITSELF, when #431 moved heaven and earth
 * to keep every per-player loader on the page behind one generation
 * counter: that counter exists because the page's loaders are ALSO called
 * from outside effects (post-write refreshes), where an effect-scoped
 * cancellation flag cannot see them. Nothing here has a write path — both
 * fetch sites live inside effects, so the effect-cleanup idiom is the
 * correct tool (the #431 header itself says so). Cross-player staleness is
 * closed by the caller instead: CoachPortal mounts this with
 * `key={playerId}`, so switching players unmounts this instance — cleanup
 * cancels its in-flight requests — and the next player starts from a blank
 * snapshot and "all" filters. Filters deliberately reset per player (a
 * season narrowed for one player is meaningless on the next roster click)
 * and are NOT persisted, unlike the player's own localStorage-backed
 * choice on Statistics.jsx.
 */
export default function CoachCareerSnapshot({ playerId }) {
  const [d, setD] = useState(null);
  // The #271 posture, same as Statistics.jsx: "we couldn't load it" is a
  // stated third state, not a card that silently isn't there (which is
  // exactly what the pre-#460 `{stats && …}` rendered on a failure).
  const [failed, setFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [season, setSeason] = useState("all");
  const [comp, setComp] = useState("all");
  const [team, setTeam] = useState("all");
  const [seasonOptions, setSeasonOptions] = useState([]);
  const [compOptions, setCompOptions] = useState([]);
  const [teamOptions, setTeamOptions] = useState([]);

  // Option lists from this player's real matches, never a hardcoded list
  // (issue #55). Failures deliberately ignored, same call as Statistics.jsx
  // and Dashboard.jsx: these populate optional dropdowns behind an "all"
  // default, so a failure costs the ability to narrow, never the
  // correctness of a displayed number.
  useEffect(() => {
    let cancelled = false;
    const qs = toStatsQuery({ playerId });
    api.get(`/matches/seasons${qs}`).then((r) => { if (!cancelled) setSeasonOptions(r.data?.seasons || []); }).catch(() => {});
    api.get(`/matches/competitions${qs}`).then((r) => { if (!cancelled) setCompOptions(r.data?.competitions || []); }).catch(() => {});
    api.get(`/matches/teams${qs}`).then((r) => { if (!cancelled) setTeamOptions(r.data?.teams || []); }).catch(() => {});
    return () => { cancelled = true; };
  }, [playerId]);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    getWithRetry(`/stats/summary${toStatsQuery({ season, competition: comp, team, playerId })}`)
      .then((r) => { if (!cancelled) setD(r.data); })
      .catch((e) => {
        if (cancelled) return;
        // Drop the previous filter's data rather than leave it on screen
        // under the new filter's heading (Statistics.jsx, #271).
        setD(null);
        setFailed(true);
        console.error("CoachCareerSnapshot: failed to load /stats/summary", e);
      });
    return () => { cancelled = true; };
  }, [playerId, season, comp, team, reloadKey]);

  const filterActive = season !== "all" || comp !== "all" || team !== "all";
  const isEmpty = filterActive && d?.matches === 0;

  // Issue #526 — the snapshot FOLDS, and arrives folded: a coach lands on
  // this page to triage a roster, not to read 24 stat tiles, and the tab
  // strip below stopped living underneath a screen of numbers. The folded
  // header carries an honest headline (`careerSnapshotSummary` — "—" until
  // the payload actually arrives, never a fabricated zero). The fold's
  // open/closed state lives inside SectionCard, so the `key={playerId}`
  // remount at the call site resets it to closed on every roster click —
  // one player's open snapshot never leaks onto the next player's name.
  // The filter bar moved INSIDE the panel: a fold's header is one <button>,
  // and three Selects cannot live inside it.
  return (
    <SectionCard
      icon={Trophy}
      title="Career snapshot"
      desc="The three dashboards the player sees, behind the same filters."
      collapsible
      defaultOpen={false}
      summary={careerSnapshotSummary({ d, failed })}
      toggleTestid="cp-snapshot-toggle"
      testid="cp-stats-snapshot"
    >
      <div className="flex justify-end min-w-0">
        <StatsFilterBar
          competition={comp}
          season={season}
          team={team}
          competitionOptions={compOptions}
          seasonOptions={seasonOptions}
          teamOptions={teamOptions}
          onCompetitionChange={setComp}
          onSeasonChange={setSeason}
          onTeamChange={setTeam}
        />
      </div>
      {failed ? (
        <LoadFailureNote
          what="this player's statistics"
          testId="cp-stats-error"
          onRetry={() => setReloadKey((k) => k + 1)}
        />
      ) : !d ? (
        <div className="text-slate-500 text-sm">Loading…</div>
      ) : isEmpty ? (
        // The Statistics page's empty-filter sentence, with the player as
        // the subject — 24 dashes would read as a career of zeros.
        <p className="text-slate-500 text-sm" data-testid="cp-stats-empty">
          No matches for {season === "all" ? "any season" : season} in {comp === "all" ? "any competition" : comp}
          {team !== "all" && <> for {team}</>}
        </p>
      ) : (
        <CareerStatSections d={d} />
      )}
    </SectionCard>
  );
}
